import { supabase } from './supabaseClient';

/**
 * Получить текущего пользователя
 * Использует Supabase Auth сессию (работает на всех доменах)
 */
async function getCurrentUser() {
  // Сначала проверяем активную сессию Supabase (работает на всех доменах)
  try {
    const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
    
    if (!authError && authUser) {
      // Пробуем teachers (для преподавателей)
      const { data: teacherData, error: teacherError } = await supabase
        .from('teachers')
        .select('*')
        .eq('auth_user_id', authUser.id)
        .maybeSingle();
      
      if (!teacherError && teacherData) {
        return {
          ...teacherData,
          id: teacherData.auth_user_id,
          role: 'teacher'
        };
      }
      
      // Пробуем students (для студентов)
      const { data: studentData, error: studentError } = await supabase
        .from('students')
        .select('*')
        .eq('user_account_id', authUser.id)
        .single();
      
      if (!studentError && studentData) {
        return {
          ...studentData,
          id: studentData.id, // UUID студента из таблицы students
          auth_user_id: authUser.id, // auth_user_id для совместимости
          role: 'student'
        };
      }
      
      // Fallback: пробуем users
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .single();
      
      if (!userError && userData) {
        return userData;
      }
    }
  } catch (e) {
    console.error('Error getting user from Supabase Auth:', e);
  }
  
  // Fallback: проверяем localStorage (только для совместимости)
  const currentUserStr = localStorage.getItem('currentUser');
  if (currentUserStr) {
    try {
      const user = JSON.parse(currentUserStr);
      if (user && user.id) {
        return user;
      }
    } catch (e) {
      console.error('Error parsing currentUser from localStorage:', e);
    }
  }
  
  return null;
}

/**
 * Конвертировать homework из localStorage формата в Supabase формат
 */
function convertHomeworkToSupabaseFormat(homework, isNew = false) {
  const result = {
    lesson_id: homework.lessonId || null,
    student_id: homework.studentId || null,
    title: homework.title || '', // Отдельная колонка
    description: homework.description || '', // Отдельная колонка
    due_date: homework.dueDate || null, // Отдельная колонка
    content: {
      title: homework.title || '', // Дублируем в content для обратной совместимости
      description: homework.description || '', // Дублируем в content для обратной совместимости
      content: homework.content || '',
      containers: homework.containers || [],
      attachments: homework.attachments || [],
      bannerImage: homework.bannerImage || null,
      dueDate: homework.dueDate || null // Дублируем в content для обратной совместимости
    },
    status: homework.status || 'pending',
    submitted_at: homework.submittedAt || null,
    reviewed_at: homework.reviewedAt || null,
    created_at: homework.assignedAt || homework.createdAt || new Date().toISOString(),
    updated_at: homework.updatedAt || new Date().toISOString()
  };
  
  // Добавляем id только если это валидный UUID (не timestamp)
  if (homework.id) {
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(homework.id));
    if (isUUID) {
      result.id = homework.id;
    } else if (!isNew) {
      // Если это не новый homework и id не UUID, не передаем id (Supabase сгенерирует новый)
      console.warn('Homework id is not a valid UUID, will be generated by Supabase:', homework.id);
    }
  }
  
  return result;
}

/**
 * Конвертировать homework из Supabase формата в localStorage формат
 */
function convertHomeworkFromSupabaseFormat(homework) {
  if (!homework) return null;

  const content = homework.content || {};
  const containers = content.containers || [];
  
  return {
    id: homework.id,
    studentId: homework.student_id || null,
    lessonId: homework.lesson_id || null,
    // Приоритет: отдельные колонки, затем content (для обратной совместимости)
    title: homework.title || content.title || '',
    description: homework.description || content.description || '',
    content: content.content || '',
    containers: containers,
    attachments: content.attachments || [],
    bannerImage: content.bannerImage || null,
    status: homework.status || 'pending',
    assignedAt: homework.created_at || new Date().toISOString(),
    dueDate: homework.due_date || content.dueDate || null, // Приоритет: отдельная колонка, затем content
    submittedAt: homework.submitted_at || null,
    reviewedAt: homework.reviewed_at || null,
    grade: homework.grade || null,
    feedback: homework.feedback || null,
    createdAt: homework.created_at || new Date().toISOString(),
    updatedAt: homework.updated_at || new Date().toISOString()
  };
}

/**
 * Fallback: загрузить homework из localStorage
 */
function getHomeworkFromLocalStorage(studentId) {
  const allHomework = [];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('homework_')) {
      try {
        const homework = JSON.parse(localStorage.getItem(key));
        if (homework && homework.studentId === studentId) {
          allHomework.push({
            id: key.replace('homework_', ''),
            ...homework
          });
        }
      } catch (e) {
        console.error('Error parsing homework:', e);
      }
    }
  }
  
  return allHomework;
}

/**
 * Получить все домашние задания студента
 */
export async function getHomework(studentId) {
  try {
    const user = await getCurrentUser();
    if (!user) {
      console.warn('User not authenticated in getHomework, studentId:', studentId);
      console.warn('This may cause issues on production. Check Supabase Auth session.');
      return getHomeworkFromLocalStorage(studentId);
    }

    const { data, error } = await supabase
      .from('homework')
      .select('*')
      .eq('student_id', studentId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching homework from Supabase:', error?.code, error?.message, error?.details ?? '');
      return getHomeworkFromLocalStorage(studentId);
    }

    return data.map(convertHomeworkFromSupabaseFormat);
  } catch (error) {
    console.error('Error in getHomework:', String(error?.message ?? error));
    return getHomeworkFromLocalStorage(studentId);
  }
}

/**
 * Получить домашнее задание по ID
 */
export async function getHomeworkById(homeworkId) {
  try {
    console.log('getHomeworkById called with:', homeworkId);
    // Fast-fail for non-UUID ids (e.g. timestamp ids generated client-side
    // for tabs that haven't been saved to Supabase yet). Without this the
    // request goes out and Postgres rejects with 22P02 "invalid input
    // syntax for type uuid", spamming the console with red errors.
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(homeworkId || ''));
    if (!isUUID) {
      const homework = localStorage.getItem(`homework_${homeworkId}`);
      return homework ? JSON.parse(homework) : null;
    }

    const user = await getCurrentUser();
    if (!user) {
      console.warn('User not authenticated, falling back to localStorage');
      const homework = localStorage.getItem(`homework_${homeworkId}`);
      return homework ? JSON.parse(homework) : null;
    }

    const { data, error } = await supabase
      .from('homework')
      .select('*')
      .eq('id', homeworkId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching homework by id:', error?.code, error?.message, error?.details ?? '');
      const homework = localStorage.getItem(`homework_${homeworkId}`);
      return homework ? JSON.parse(homework) : null;
    }

    if (!data) {
      console.warn('Homework not found in Supabase:', homeworkId);
      return null;
    }

    console.log('Homework found in Supabase:', data);
    console.log('[RAW DB] container[0].content:', JSON.stringify(data?.content?.containers?.[0]?.content ?? null));
    return convertHomeworkFromSupabaseFormat(data);
  } catch (error) {
    console.error('Error in getHomeworkById:', String(error?.message ?? error));
    const homework = localStorage.getItem(`homework_${homeworkId}`);
    return homework ? JSON.parse(homework) : null;
  }
}

/**
 * Создать домашнее задание
 */
export async function createHomework(homeworkData) {
  try {
    const user = await getCurrentUser();
    if (!user) {
      console.warn('User not authenticated, cannot save homework to Supabase');
      throw new Error('User not authenticated');
    }

    const supabaseHomework = convertHomeworkToSupabaseFormat(homeworkData, true);
    supabaseHomework.updated_at = new Date().toISOString();

    if (!supabaseHomework.student_id) {
      throw new Error('Укажите студента (студент должен быть в Supabase). Откройте урок из профиля студента или добавьте ?studentId=... в URL.');
    }
    if (!supabaseHomework.lesson_id) {
      throw new Error('Сначала сохраните урок (кнопка Save), затем отправьте домашнее задание.');
    }

    const { data, error } = await supabase
      .from('homework')
      .insert(supabaseHomework)
      .select()
      .single();

    if (error) {
      const msg = [error.message, error.code, error.details].filter(Boolean).join(' ') || 'Unknown Supabase error';
      console.error('Error creating homework in Supabase:', error.code, error.message, error.details ?? '');
      throw new Error(msg);
    }

    const savedHomework = convertHomeworkFromSupabaseFormat(data);
    return savedHomework;
  } catch (error) {
    console.error('Error in createHomework:', String(error?.message ?? error));
    throw error;
  }
}

/**
 * Обновить домашнее задание
 */
export async function updateHomework(homeworkId, updates) {
  try {
    const user = await getCurrentUser();
    console.log('updateHomework - current user:', user);
    if (!user) {
      console.warn('User not authenticated, cannot update homework in Supabase');
      throw new Error('User not authenticated');
    }

    // Формируем content объект (для обратной совместимости)
    let contentUpdate = undefined;
    if (updates.content || updates.title || updates.description || updates.containers || updates.dueDate !== undefined) {
      contentUpdate = {
        ...(updates.content || {}),
        title: updates.title !== undefined ? updates.title : (updates.content?.title || ''),
        description: updates.description !== undefined ? updates.description : (updates.content?.description || ''),
        content: updates.content?.content || updates.content || '',
        containers: updates.containers || updates.content?.containers || [],
        attachments: updates.attachments || updates.content?.attachments || [],
        bannerImage: updates.bannerImage || updates.content?.bannerImage || null,
        dueDate: updates.dueDate !== undefined ? updates.dueDate : (updates.content?.dueDate || updates.due_date || null)
      };
    }
    
    const supabaseUpdates = {
      // Отдельные колонки (приоритет)
      title: updates.title !== undefined ? updates.title : undefined,
      description: updates.description !== undefined ? updates.description : undefined,
      due_date: updates.dueDate !== undefined ? updates.dueDate : (updates.due_date !== undefined ? updates.due_date : undefined),
      // Content для обратной совместимости
      ...(contentUpdate ? { content: contentUpdate } : {}),
      status: updates.status,
      grade: updates.grade,
      feedback: updates.feedback,
      submitted_at: updates.submittedAt,
      reviewed_at: updates.reviewedAt || (updates.status === 'reviewed' ? new Date().toISOString() : undefined),
      updated_at: new Date().toISOString()
    };

    // Удаляем undefined поля
    Object.keys(supabaseUpdates).forEach(key => {
      if (supabaseUpdates[key] === undefined) {
        delete supabaseUpdates[key];
      }
    });

    console.log('updateHomework called with:', { homeworkId, supabaseUpdates, userRole: user.role });
    console.log('[RAW WRITE] supabaseUpdates.content.containers[0].content:', JSON.stringify(supabaseUpdates?.content?.containers?.[0]?.content ?? null));

    // Для студентов проверяем, что homework принадлежит им
    let checkHomework = null;
    if (user.role === 'student') {
      const { data: homeworkCheck } = await supabase
        .from('homework')
        .select('student_id')
        .eq('id', homeworkId)
        .maybeSingle();
      
      checkHomework = homeworkCheck;
      
      console.log('Homework student_id check:', checkHomework);
      console.log('Current user data:', { 
        userId: user.id, 
        userAccountId: user.user_account_id,
        authUserId: user.auth_user_id,
        allUserFields: Object.keys(user)
      });
      
      // user.id теперь содержит UUID студента из таблицы students
      if (checkHomework && checkHomework.student_id && checkHomework.student_id !== user.id) {
        console.error('Student ID mismatch:', {
          homeworkStudentId: checkHomework.student_id,
          userStudentId: user.id
        });
        throw new Error('You can only update your own homework');
      }
      
      console.log('Student ID match confirmed. Proceeding with update.');
    }

    // Выполняем update
    // Для студентов добавляем фильтр по student_id, чтобы RLS политика правильно работала
    let updateQuery = supabase
      .from('homework')
      .update(supabaseUpdates)
      .eq('id', homeworkId);
    
    if (user.role === 'student') {
      // Добавляем фильтр по student_id для студентов, чтобы RLS политика понимала контекст
      updateQuery = updateQuery.eq('student_id', user.id);
      console.log('Updating homework for student:', {
        studentId: user.id,
        homeworkId: homeworkId,
        updates: supabaseUpdates
      });
      
      // Проверяем auth.uid() для отладки
      const { data: { user: authUser } } = await supabase.auth.getUser();
      console.log('Auth user ID:', authUser?.id);
      console.log('Student user_account_id:', user.user_account_id);
    }
    
    // Выполняем update с select, чтобы получить обновленные данные и проверить, что обновление прошло
    const { data: updatedData, error: updateError, count } = await updateQuery.select();
    
    console.log('Update result:', { error: updateError, count, dataLength: updatedData?.length });

    if (updateError) {
      console.error('Error updating homework in Supabase:', updateError);
      throw updateError;
    }

    // Если нет данных, значит ничего не обновилось (RLS блокирует)
    if (!updatedData || updatedData.length === 0) {
      console.error('Homework update returned 0 rows. Possible RLS issue.');
      
      // Для студентов проверяем связь через students таблицу
      if (user.role === 'student') {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        console.log('🔍 RLS Debug Info:', {
          authUid: authUser?.id,
          userAccountId: user.user_account_id,
          studentId: user.id,
          homeworkStudentId: checkHomework?.student_id,
          match: authUser?.id === user.user_account_id
        });
        
        // Проверяем, существует ли студент с таким user_account_id
        const { data: studentCheck } = await supabase
          .from('students')
          .select('id, user_account_id')
          .eq('user_account_id', authUser?.id)
          .maybeSingle();
        
        console.log('Student record check:', studentCheck);
        
        if (studentCheck && studentCheck.id !== checkHomework?.student_id) {
          console.error('Student ID mismatch:', {
            studentRecordId: studentCheck.id,
            homeworkStudentId: checkHomework?.student_id
          });
        }
      }
      
      // Проверяем, существует ли homework и принадлежит ли студенту
      const { data: checkData, error: checkError } = await supabase
        .from('homework')
        .select('id, student_id')
        .eq('id', homeworkId)
        .maybeSingle();
      
      console.log('Homework check result:', checkData);
      
      if (checkError || !checkData) {
        throw new Error(`Homework with id ${homeworkId} not found or access denied`);
      } else if (user.role === 'student' && checkData.student_id !== user.id) {
        throw new Error(`You can only update your own homework`);
      } else {
        throw new Error(`Homework update failed - RLS policy is blocking the update. Please check Supabase RLS policies. Make sure SQL script SUPABASE_HOMEWORK_RLS_SETUP.sql is executed.`);
      }
    }

    // Используем обновленные данные из результата update
    const updatedHomework = updatedData[0];

    const savedHomework = convertHomeworkFromSupabaseFormat(updatedHomework);
    return savedHomework;
  } catch (error) {
    console.error('Error in updateHomework:', error);
    throw error;
  }
}

/**
 * Отправить домашнее задание студентом
 */
export async function submitHomework(homeworkId, content) {
  try {
    return await updateHomework(homeworkId, {
      content: content.content || content,
      containers: content.containers,
      status: 'submitted',
      submittedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error in submitHomework:', error);
    throw error;
  }
}

/**
 * Проверить домашнее задание преподавателем
 */
export async function reviewHomework(homeworkId, grade, feedback) {
  try {
    return await updateHomework(homeworkId, {
      status: 'reviewed',
      grade: grade,
      feedback: feedback,
      reviewedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error in reviewHomework:', error);
    throw error;
  }
}

/**
 * Удалить домашнее задание
 */
export async function deleteHomework(homeworkId) {
  try {
    const user = await getCurrentUser();
    if (!user) {
      console.warn('User not authenticated, falling back to localStorage');
      localStorage.removeItem(`homework_${homeworkId}`);
      return true;
    }

    const { error } = await supabase
      .from('homework')
      .delete()
      .eq('id', homeworkId);

    if (error) {
      console.error('Error deleting homework from Supabase:', error);
      localStorage.removeItem(`homework_${homeworkId}`);
      return true;
    }

    // Также удаляем из localStorage
    localStorage.removeItem(`homework_${homeworkId}`);

    return true;
  } catch (error) {
    console.error('Error in deleteHomework:', error);
    localStorage.removeItem(`homework_${homeworkId}`);
    return true;
  }
}
